import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import BrandsCarousel from '../components/BrandsCarousel';
import { Reveal } from '../components/Reveal';
import { Award, Users, Cpu, Clock, ShieldCheck, Sparkles, Lightbulb, Heart } from 'lucide-react';

const EASE = [0.16, 1, 0.3, 1];

const AboutPage = () => {
  const { language } = useLanguage();

  const content = {
    es: {
      badge: 'Sobre Nosotros',
      title1: 'La excelencia detrás de cada',
      title2: 'espacio inteligente',
      subtitle: 'Más de una década transformando hogares y negocios en entornos donde la tecnología trabaja de forma invisible para mejorar tu vida.',
      storyBadge: 'Nuestra Historia',
      storyTitle: 'Tecnología que no se ve, pero se siente',
      storyParagraphs: [
        'En Domodomotics creemos que la mejor tecnología es aquella que desaparece en el fondo y simplemente funciona. Durante más de una década hemos estado a la vanguardia de la automatización inteligente, transformando espacios ordinarios en entornos extraordinarios.',
        'Nuestro equipo combina conocimientos profundos en ingeniería, diseño y tecnología para crear soluciones que se adaptan perfectamente a tu estilo de vida, sin que tengas que adaptarte a ellas.',
        'Cada proyecto es una oportunidad para superar expectativas: ofrecemos no solo automatización, sino una experiencia de vida mejorada donde el confort, la eficiencia y la elegancia convergen.',
      ],
      statsBadge: 'En Cifras',
      stats: [
        { value: '150+', label: 'Proyectos completados', icon: Award },
        { value: '10+', label: 'Años de experiencia', icon: Clock },
        { value: '200+', label: 'Clientes satisfechos', icon: Users },
        { value: '24/7', label: 'Soporte técnico', icon: ShieldCheck },
      ],
      valuesBadge: 'Nuestros Valores',
      valuesTitle: 'Lo que nos define',
      values: [
        { title: 'Innovación constante', desc: 'Integramos las últimas tecnologías del mercado para ofrecer soluciones siempre a la vanguardia.', icon: Cpu },
        { title: 'Diseño a medida', desc: 'Cada instalación se diseña desde cero para adaptarse a tu espacio, tus rutinas y tus necesidades.', icon: Lightbulb },
        { title: 'Calidad sin concesiones', desc: 'Trabajamos solo con las mejores marcas y aplicamos los más altos estándares de instalación.', icon: Sparkles },
        { title: 'Trato cercano', desc: 'Te acompañamos antes, durante y después del proyecto con un servicio personal y honesto.', icon: Heart },
      ],
      ctaTitle: '¿Listo para transformar tu espacio?',
      ctaSubtitle: 'Contáctanos para una consulta gratuita y descubre todo lo que la domótica puede hacer por ti.',
      ctaButton: 'Hablemos de tu proyecto',
      ctaProjects: 'Ver Proyectos',
    },
    en: {
      badge: 'About Us',
      title1: 'The excellence behind every',
      title2: 'intelligent space',
      subtitle: 'Over a decade transforming homes and businesses into environments where technology works invisibly to improve your life.',
      storyBadge: 'Our Story',
      storyTitle: 'Technology you don\'t see, but feel',
      storyParagraphs: [
        'At Domodomotics we believe the best technology is the kind that fades into the background and simply works. For over a decade we have been at the forefront of intelligent automation, transforming ordinary spaces into extraordinary environments.',
        'Our team combines deep expertise in engineering, design and technology to create solutions that adapt perfectly to your lifestyle, so you never have to adapt to them.',
        'Every project is an opportunity to exceed expectations: we deliver not just automation, but an enhanced living experience where comfort, efficiency and elegance converge.',
      ],
      statsBadge: 'By the Numbers',
      stats: [
        { value: '150+', label: 'Completed projects', icon: Award },
        { value: '10+', label: 'Years of experience', icon: Clock },
        { value: '200+', label: 'Satisfied clients', icon: Users },
        { value: '24/7', label: 'Technical support', icon: ShieldCheck },
      ],
      valuesBadge: 'Our Values',
      valuesTitle: 'What defines us',
      values: [
        { title: 'Constant innovation', desc: 'We integrate the latest technologies on the market to deliver solutions that are always cutting-edge.', icon: Cpu },
        { title: 'Bespoke design', desc: 'Every installation is designed from scratch to fit your space, your routines and your needs.', icon: Lightbulb },
        { title: 'Uncompromising quality', desc: 'We work only with the best brands and apply the highest installation standards.', icon: Sparkles },
        { title: 'A personal touch', desc: 'We stand by you before, during and after the project with honest, personal service.', icon: Heart },
      ],
      ctaTitle: 'Ready to transform your space?',
      ctaSubtitle: 'Contact us for a free consultation and discover everything home automation can do for you.',
      ctaButton: "Let's talk about your project",
      ctaProjects: 'View Projects',
    },
  };

  const c = content[language] || content.es;

  return (
    <div className="min-h-screen bg-obsidian" data-testid="about-page">
      {/* Hero */}
      <section className="relative h-[64vh] min-h-[500px] flex items-center overflow-hidden noise-overlay">
        <div className="absolute inset-0">
          <motion.img
            src="https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=1600&q=85"
            alt="Domodomotics"
            className="w-full h-full object-cover"
            initial={{ scale: 1.12, opacity: 0 }}
            animate={{ scale: 1, opacity: 0.4 }}
            transition={{ duration: 1.5, ease: EASE }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/75 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-obsidian/40" />
        </div>
        <div className="absolute -top-16 right-0 w-[34rem] h-[34rem] bg-brand/15 rounded-full blur-[140px]" />

        <div className="relative z-10 container mx-auto px-6 lg:px-10">
          <motion.p className="overline mb-5" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE, delay: 0.2 }}>
            {c.badge}
          </motion.p>
          <motion.h1
            className="font-heading text-4xl md:text-5xl lg:text-6xl font-extralight text-white tracking-tight leading-[1.02] max-w-3xl"
            initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 0.3 }}
          >
            {c.title1}{' '}
            <span className="text-gradient-blue font-normal">{c.title2}</span>
          </motion.h1>
          <motion.p className="mt-6 text-lg text-white/60 max-w-2xl leading-relaxed" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 0.45 }}>
            {c.subtitle}
          </motion.p>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 md:py-32 bg-obsidian relative overflow-hidden">
        <div className="absolute bottom-0 right-0 w-[34rem] h-[34rem] bg-brand/8 rounded-full blur-[140px] pointer-events-none" />
        <div className="container mx-auto px-6 lg:px-10 relative z-10">
          <div className="grid md:grid-cols-2 gap-16 lg:gap-24 items-center">
            <Reveal>
              <p className="overline mb-5">{c.storyBadge}</p>
              <h2 className="font-heading text-3xl md:text-4xl font-light text-white mb-8 tracking-tight leading-tight">{c.storyTitle}</h2>
              <div className="space-y-6 text-white/55 leading-relaxed">
                {c.storyParagraphs.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </Reveal>
            <motion.div
              initial={{ opacity: 0, scale: 1.05 }} whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: '-80px' }} transition={{ duration: 1, ease: EASE }}
              className="relative"
            >
              <div className="absolute -inset-4 bg-brand/10 rounded-3xl blur-2xl" />
              <img src="https://images.unsplash.com/photo-1708113388262-17fdf0e21205?w=900&q=80" alt="Domodomotics team" className="relative rounded-2xl w-full h-[460px] object-cover shadow-2xl" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-surface border-t border-white/10">
        <div className="container mx-auto px-6 lg:px-10">
          <Reveal className="text-center mb-14">
            <p className="overline">{c.statsBadge}</p>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {c.stats.map((stat, i) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={i}
                  className="glass-card p-8 rounded-2xl text-center"
                  data-testid={`about-stat-${i}`}
                  initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }} transition={{ duration: 0.6, ease: EASE, delay: i * 0.08 }}
                >
                  <div className="w-12 h-12 mx-auto rounded-xl bg-brand/10 border border-brand/20 flex items-center justify-center text-brand-light mb-5">
                    <Icon size={24} strokeWidth={1.5} />
                  </div>
                  <p className="font-heading text-4xl font-light text-white mb-2">{stat.value}</p>
                  <p className="text-white/50 text-sm">{stat.label}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 md:py-28 bg-obsidian">
        <div className="container mx-auto px-6 lg:px-10">
          <SectionHeadingInline overline={c.valuesBadge} title={c.valuesTitle} />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {c.values.map((value, i) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={i}
                  className="glass-card hover-lift p-8 rounded-2xl group"
                  data-testid={`about-value-${i}`}
                  initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }} transition={{ duration: 0.6, ease: EASE, delay: i * 0.08 }}
                >
                  <div className="w-12 h-12 rounded-xl bg-brand/10 border border-brand/20 flex items-center justify-center text-brand-light mb-6 group-hover:bg-brand group-hover:text-white transition-all duration-400">
                    <Icon size={24} strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading text-lg font-medium text-white mb-3">{value.title}</h3>
                  <p className="text-white/50 text-sm leading-relaxed">{value.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Brands / Partners */}
      <BrandsCarousel background="bg-surface" />

      {/* CTA */}
      <section className="py-24 bg-obsidian border-t border-white/10">
        <div className="container mx-auto px-6 lg:px-10">
          <Reveal className="glass-card p-12 md:p-16 rounded-3xl text-center max-w-4xl mx-auto">
            <h2 className="font-heading text-3xl md:text-4xl font-light text-white mb-6 tracking-tight">{c.ctaTitle}</h2>
            <p className="text-white/55 text-lg mb-10 max-w-2xl mx-auto">{c.ctaSubtitle}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contacto" className="btn-primary px-10 py-4 rounded-sm text-sm uppercase tracking-wider font-medium" data-testid="about-cta-contact">{c.ctaButton}</Link>
              <Link to="/proyectos" className="btn-ghost px-10 py-4 rounded-sm text-sm uppercase tracking-wider font-medium" data-testid="about-cta-projects">{c.ctaProjects}</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

const SectionHeadingInline = ({ overline, title }) => (
  <Reveal className="text-center mb-14 max-w-2xl mx-auto">
    <p className="overline mb-5">{overline}</p>
    <h2 className="font-heading text-3xl md:text-4xl font-light text-white tracking-tight">{title}</h2>
  </Reveal>
);

export default AboutPage;
